import { LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateUnitApi from "../../../api/units/CreateUnitApi";
import GetHeadUnitsApi from "../../../api/GetHeadUnitsApi";
import { GetOrgLevelsToAddUnitApi } from "../../../api/GetOrgLevelsToAddUnitApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import PageLoader from "../../sub/PageLoader";
import { useSelector } from "react-redux";
import { GetApproversApi } from "../../../api/approvals/GetApproversApi";

const { Option } = Select;

function AddUnitModal(props) {
  const [headUnits, setHeadUnits] = useState([]);
  const [organizationLevels, setOrganizationLevels] = useState();
  const [submit, setSubmit] = useState(false);
  const [canAddHeadUnit, setCanAddHeadUnit] = useState(false);
  const [levelId, setLevelId] = useState("");
  const [headUnitId, setHeadUnitId] = useState("");
  const positionState = useSelector((state) => state.positionState.value);
  let navigate = useNavigate();
  const [approvers, setApprovers] = useState([]);

  function handleCancel() {
    setHeadUnits([]);
    setOrganizationLevels();
    setSubmit(false);
    setCanAddHeadUnit(false);
    setLevelId("");
    setHeadUnitId("");
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getOrganizationLevels();
      getApprovers();
    }
  }, [props.visible]);

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
  }

  async function getOrganizationLevels() {
    const apiResponse = await GetOrgLevelsToAddUnitApi();
    if (apiResponse.message === "success") {
      setOrganizationLevels(apiResponse.levels);
    }
  }

  async function onFinish(value) {
    setSubmit(true);
    const apiResponse = await CreateUnitApi(
      value.unitName,
      headUnitId,
      levelId,
      positionState?.id,
      positionState?.userRole?.name === "Manager"
        ? value.approver
        : positionState.id
    );

    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Unit added successfully");
      props.handleOk();
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't add unit. Try again later."
      );
    }
    handleCancel();
  }

  function selectLevel(value) {
    setLevelId(value);
    let order;
    let id;
    organizationLevels.forEach((level) => {
      if (level.id == value) {
        order = level.order;
        id = level.id;
      }
    });
    if (order != 1) {
      setCanAddHeadUnit(true);
      getHeadUnits(id);
    } else {
      setCanAddHeadUnit(false);
    }
  }

  async function getHeadUnits(id) {
    const apiResponse = await GetHeadUnitsApi(id);
    if (apiResponse.success) {
      setHeadUnits(apiResponse.data);
    }
  }

  function selectHeadUnit(value) {
    setHeadUnitId(value);
  }

  function showAddOrgLevels() {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            Add organizational levels
          </div>
          <div className="mb-3 text-gray-400">
            You must add more org levels before adding any more units
          </div>
          <Button
            onClick={() => {
              navigate("/setup-account/general/org-levels");
              handleCancel();
            }}
            type="primary"
          >
            Add org levels
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Modal
      title="Add new unit"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      {organizationLevels ? (
        !!organizationLevels.length ? (
          <Form
            name="addUnit"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            autoComplete="off"
          >
            <Form.Item
              label="Org level*"
              name="orgLevel"
              rules={[
                {
                  required: true,
                  message: "Please select a level!",
                },
              ]}
            >
              <Select
                onSelect={(option) => selectLevel(option)}
                placeholder="Select org level"
              >
                {organizationLevels.map((level) => {
                  return (
                    <Option key={level.id} value={level.id}>
                      {level.name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              label="Unit name*"
              name="unitName"
              rules={[
                {
                  required: true,
                  message: "Please enter name!",
                },
              ]}
            >
              <Input placeholder="Unit name" />
            </Form.Item>

            {canAddHeadUnit && (
              <Form.Item
                label="Head unit*"
                name="headUnit"
                rules={[
                  {
                    required: true,
                    message: "Please select head unit!",
                  },
                ]}
              >
                <Select
                  showSearch
                  onSelect={(option) => selectHeadUnit(option)}
                  placeholder="Head unit"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {headUnits.map((unit) => {
                    return (
                      <Option key={unit.id} value={unit.id}>
                        {unit.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}
            {positionState?.userRole?.name === "Manager" && (
              <Form.Item
                label="Approver"
                name="approver"
                rules={[
                  {
                    required: true,
                    message: "Please select approver!",
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select approver"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {approvers?.map((approver) => {
                    return (
                      <Option key={approver?.id} value={approver?.id}>
                        {(approver?.designations?.abbreviation ||
                          approver?.designations?.name) +
                          "," +
                          approver?.units?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 0 }}>
              <div className=" flex justify-end">
                <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                  Cancel
                </Button>
                <Button
                  className="btn w-28"
                  type="primary"
                  disabled={submit}
                  htmlType={submit && "submit"}
                >
                  {submit ? (
                    <Spin
                      size="small"
                      indicator={
                        <LoadingOutlined style={{ color: "#fff" }} spin />
                      }
                    />
                  ) : (
                    "Add"
                  )}
                </Button>
              </div>
            </Form.Item>
          </Form>
        ) : (
          showAddOrgLevels()
        )
      ) : (
        <PageLoader />
      )}
    </Modal>
  );
}

export default AddUnitModal;
